/**
 * **A referral typed in by hand** (Pete, 2026-09-24). Some branches take
 * referrals by phone or fax, and a prospect with no referral system needs to see
 * one arrive anyway — so intake keys it in and the queue treats it like any
 * other.
 *
 * The patient is created **for real** through the product's own mutation, which
 * geocodes the address on the way in. That is not incidental: the referral panel
 * asks the product's scheduler who can take the start of care, and the scheduler
 * places clinicians by how far they are from the patient. A patient with no
 * coordinates has no options.
 */
import { useMemo, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
// Grid2, as the rest of the app uses where it needs the `size` API.
import Grid from "@mui/material/Grid2";
import { PatientSexEnum } from "~/graphql/__generated__/graphql";
import { addWorkingDaysToKey, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { planTemplates } from "../../engine/expectedPlan";
import { addKeyedReferral } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import AddressForm from "~/components/common/forms/AddressForm";
import { emptyAddress, type AddressData } from "@caredna-marlin/utils/client/geocoding";

export function KeyedReferralDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded?: (ref: string) => void }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const templates = useMemo(() => planTemplates(state.config), [state.config]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  // The product's own address control, so a keyed referral is geocoded the way a
  // hand-entered patient is anywhere else in the app — and so the branch types
  // one line rather than four boxes (Pete, 2026-09-24).
  const [address, setAddress] = useState<AddressData>(emptyAddress);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    sex: "" as "" | PatientSexEnum,
    dateOfBirth: "",
    payer: "MA" as "MA" | "FFS",
    source: "",
    // The next working day, which is what a hospital discharge usually says.
    expectedDischarge: addWorkingDaysToKey(toDateKey(selectDemoNow(state, new Date()), tz), 1),
    caseType: templates[0]?.key ?? "",
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  // What the branch cannot place somebody without: a name to call them, an
  // address to drive to, and what was ordered. The ZIP matters twice over —
  // it is what puts them in a territory.
  const ready = !!form.firstName.trim() && !!form.lastName.trim() && !!address.addressStreet && !!address.addressZip && !!form.source.trim() && !!form.caseType;

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const ref = await addKeyedReferral({
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        source: form.source.trim(),
        sex: form.sex || undefined,
        addressStreet: address.addressStreet ?? "",
        addressLineTwo: address.addressLineTwo ?? undefined,
        addressCity: address.addressCity ?? "",
        addressState: address.addressState ?? "",
        addressZip: address.addressZip ?? "",
        addressPlaceId: address.addressPlaceId ?? undefined,
      });
      onAdded?.(ref);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New referral</DialogTitle>
      <DialogContent>
        {/* **Room for the floating labels.** MUI zeroes a DialogContent's top
            padding when it follows a DialogTitle, and an outlined label sits
            above its own border — so the first row's "First name" and "Last
            name" were clipped by the content's edge the moment they floated up
            (Pete, 2026-09-24). */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 6 }}>
            <TextField required fullWidth size="small" label="First name" value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField required fullWidth size="small" label="Last name" value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
          </Grid>
          {/* `AddressForm` hands back two fields, and they space themselves: the
              autocomplete carries MUI's `dense` margins and the Apt line carries
              none, so inside one grid cell they sat 4px apart while every other
              row sat 16. The cell lays them out itself instead — margins off,
              one gap, the same gap the grid uses. */}
          <Grid size={{ xs: 12 }} sx={{ display: "flex", flexDirection: "column", gap: 2, "& .MuiFormControl-root": { my: 0 } }}>
            <AddressForm required size="small" variant="outlined" address={address} setAddress={setAddress} disabled={busy} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth size="small" select label="Sex" value={form.sex} onChange={(e) => set({ sex: e.target.value as PatientSexEnum })}>
              <MenuItem value="">Not given</MenuItem>
              <MenuItem value={PatientSexEnum.Female}>Female</MenuItem>
              <MenuItem value={PatientSexEnum.Male}>Male</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth size="small" type="date" label="Date of birth" value={form.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField required fullWidth size="small" label="Referred from" value={form.source} onChange={(e) => set({ source: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth size="small" select label="Payer" value={form.payer} onChange={(e) => set({ payer: e.target.value as "MA" | "FFS" })}>
              <MenuItem value="MA">Medicare Advantage</MenuItem>
              <MenuItem value="FFS">Medicare FFS</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth size="small" type="date" label="Expected discharge" value={form.expectedDischarge} onChange={(e) => set({ expectedDischarge: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField required fullWidth size="small" select label="Expected plan" value={form.caseType} onChange={(e) => set({ caseType: e.target.value })}>
              {templates.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {error && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!ready || busy}>
          {busy ? "Adding…" : "Add referral"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
